import type { LeaseCompFieldRule } from "@/features/survey/types/survey.core.types";
import type {
  LeaseComp,
  LeaseCompMediaFile,
} from "@/features/survey/types/survey.leaseComp.types";
import {
  addItemWithOrder,
  removeByIdAndReorder,
  reorderArrayItems,
} from "@/features/survey/utils/reducerFactory";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export interface LeaseCompsState {
  items: LeaseComp[];
  fieldRules: LeaseCompFieldRule[];
  currentLeaseCompId: string | null;
  isDrawerOpen: boolean;
  isLoading: boolean;
  isFieldRulesLoading: boolean;
  error: string | null;
  mediaFiles: {
    [leaseCompId: string]: {
      images: LeaseCompMediaFile[];
    };
  };
}

const initialState: LeaseCompsState = {
  items: [],
  fieldRules: [],
  currentLeaseCompId: null,
  isDrawerOpen: false,
  isLoading: false,
  isFieldRulesLoading: false,
  error: null,
  mediaFiles: {},
};

const leaseCompsSlice = createSlice({
  name: "leaseComps",
  initialState,
  reducers: {
    setLeaseComps: (state, action: PayloadAction<LeaseComp[]>) => {
      state.items = action.payload;
      state.error = null;
    },

    addLeaseComp: (state, action: PayloadAction<LeaseComp>) => {
      state.items = addItemWithOrder(state.items, action.payload);
    },

    updateLeaseComp: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        updates: Partial<LeaseComp>;
      }>,
    ) => {
      const { leaseCompId, updates } = action.payload;
      const idx = state.items.findIndex((lc) => lc.leaseCompId === leaseCompId);
      if (idx !== -1) {
        const existing = state.items[idx];
        state.items[idx] = {
          ...existing,
          ...updates,
          // Deep-merge leaseCompFields so a partial update (e.g. one address
          // field at a time from autofill) never wipes the other fields.
          leaseCompFields: updates.leaseCompFields
            ? { ...existing.leaseCompFields, ...updates.leaseCompFields }
            : existing.leaseCompFields,
        };
      }
    },

    removeLeaseComp: (state, action: PayloadAction<string>) => {
      state.items = removeByIdAndReorder(
        state.items,
        action.payload,
        "leaseCompId",
      );
    },

    reorderLeaseComps: (
      state,
      action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>,
    ) => {
      const { sourceIndex, destinationIndex } = action.payload;
      state.items = reorderArrayItems(
        state.items,
        sourceIndex,
        destinationIndex,
      );
    },

    setLeaseCompFieldRules: (
      state,
      action: PayloadAction<LeaseCompFieldRule[]>,
    ) => {
      state.fieldRules = action.payload;
    },

    updateLeaseCompFieldRule: (
      state,
      action: PayloadAction<{ key: string; isActive: boolean; order: number }>,
    ) => {
      const { key, isActive, order } = action.payload;
      const field = state.fieldRules.find((f) => f.key === key);
      if (field) {
        field.isActive = isActive;
        field.order = order;
      }
    },

    updateLeaseCompFieldRuleLabel: (
      state,
      action: PayloadAction<{ key: string; displayName: string }>,
    ) => {
      const { key, displayName } = action.payload;
      const field = state.fieldRules.find((f) => f.key === key);
      if (field) {
        field.displayName = displayName;
      }
    },

    reorderLeaseCompField: (
      state,
      action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>,
    ) => {
      const { sourceIndex, destinationIndex } = action.payload;

      // Work only on active fields sorted by order (mirrors visibleFields in the component)
      const activeFields = [...state.fieldRules]
        .filter((f) => f.isActive)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const [moved] = activeFields.splice(sourceIndex, 1);
      activeFields.splice(destinationIndex, 0, moved);

      // Re-assign order values — activeFields elements are Immer draft proxies,
      // so direct mutation is sufficient and correct.
      activeFields.forEach((item, idx) => {
        item.order = idx;
      });
    },

    setCurrentLeaseCompId: (
      state,
      action: PayloadAction<{ leaseCompId: string | null } | null>,
    ) => {
      state.currentLeaseCompId = action.payload
        ? action.payload.leaseCompId
        : null;
    },

    setOpenLeaseCompsDrawer: (state, action: PayloadAction<boolean>) => {
      state.isDrawerOpen = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setFieldRulesLoading: (state, action: PayloadAction<boolean>) => {
      state.isFieldRulesLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearLeaseComps: (state) => {
      state.items = [];
      state.fieldRules = [];
      state.currentLeaseCompId = null;
      state.isDrawerOpen = false;
      state.isLoading = false;
      state.isFieldRulesLoading = false;
      state.error = null;
      state.mediaFiles = {};
    },

    setLeaseCompMediaFiles: (
      state,
      action: PayloadAction<{
        [leaseCompId: string]: { images: LeaseCompMediaFile[] };
      }>,
    ) => {
      state.mediaFiles = action.payload;
    },

    setLeaseCompMedia: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        images: LeaseCompMediaFile[];
      }>,
    ) => {
      const { leaseCompId, images } = action.payload;
      if (!state.mediaFiles[leaseCompId]) {
        state.mediaFiles[leaseCompId] = { images: [] };
      }
      state.mediaFiles[leaseCompId].images = images;
    },

    addLeaseCompImage: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        mediaFile: LeaseCompMediaFile;
      }>,
    ) => {
      const { leaseCompId, mediaFile } = action.payload;
      if (!state.mediaFiles[leaseCompId]) {
        state.mediaFiles[leaseCompId] = { images: [] };
      }
      state.mediaFiles[leaseCompId].images.push(mediaFile);
    },

    removeLeaseCompImage: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        mediaId: string;
      }>,
    ) => {
      const { leaseCompId, mediaId } = action.payload;
      if (state.mediaFiles[leaseCompId]) {
        state.mediaFiles[leaseCompId].images = state.mediaFiles[
          leaseCompId
        ].images.filter((m) => m.mediaId !== mediaId);
      }
    },

    reorderLeaseCompImages: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        sourceIndex: number;
        destinationIndex: number;
      }>,
    ) => {
      const { leaseCompId, sourceIndex, destinationIndex } = action.payload;
      if (state.mediaFiles[leaseCompId]) {
        const [moved] = state.mediaFiles[leaseCompId].images.splice(
          sourceIndex,
          1,
        );
        state.mediaFiles[leaseCompId].images.splice(destinationIndex, 0, moved);
      }
    },

    updateLeaseCompImageVisibility: (
      state,
      action: PayloadAction<{
        leaseCompId: string;
        mediaId: string;
        isHidden: boolean;
      }>,
    ) => {
      const { leaseCompId, mediaId, isHidden } = action.payload;
      if (state.mediaFiles[leaseCompId]) {
        const image = state.mediaFiles[leaseCompId].images.find(
          (m) => m.mediaId === mediaId,
        );
        if (image) {
          image.isHidden = isHidden;
        }
      }
    },
  },
});

export const {
  setLeaseComps,
  addLeaseComp,
  updateLeaseComp,
  removeLeaseComp,
  reorderLeaseComps,
  setLeaseCompFieldRules,
  updateLeaseCompFieldRule,
  updateLeaseCompFieldRuleLabel,
  reorderLeaseCompField,
  setCurrentLeaseCompId,
  setOpenLeaseCompsDrawer,
  setLoading,
  setFieldRulesLoading,
  setError,
  clearLeaseComps,
  setLeaseCompMediaFiles,
  setLeaseCompMedia,
  addLeaseCompImage,
  removeLeaseCompImage,
  reorderLeaseCompImages,
  updateLeaseCompImageVisibility,
} = leaseCompsSlice.actions;

export default leaseCompsSlice.reducer;
